/**
 * SV Cloud PostHog client.
 *
 * NOTHING HERE RUNS UNTIL consent.ts SAYS SO. PostHog is not initialised at
 * page load: startAnalytics() initialises it the first time analyticsAllowed()
 * is true (the visitor pressed Allow, no Global Privacy Control, an eligible
 * account, not an impersonated session, not a token-carrying page), and
 * stopAnalytics() opts out and clears its storage the moment that stops being
 * true. Until then no request leaves the browser and nothing is stored.
 *
 * NO SESSION REPLAY, by decision (California Penal Code 631 exposure). Do not
 * re-enable recording, console log capture or network payload capture without
 * revisiting that decision; see the repo CLAUDE.md cloud-web analytics bullet.
 *
 * NO PERSONAL DATA IN EVENTS. identifyUser sends the account id and settings
 * only, autocapture masks element text and attributes, and before_send strips
 * query strings, fragments, token path segments and any email or name
 * property a caller passes (consent.ts sanitizeProperties).
 */

import posthog from 'posthog-js';
import type {
  ApiErrorPayload,
  DeadClickPayload,
  FormAbandonedPayload,
  FormSubmittedPayload,
  InactivityStuckPayload,
  ProjectGroupTraits,
  UserTraits,
} from './types';
import { sanitizeProperties } from './consent';

export const POSTHOG_KEY = (import.meta.env.VITE_POSTHOG_KEY as string | undefined) ?? '';
export const POSTHOG_HOST =
  (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ?? 'https://d.sv-academy.org';

const IS_MOCK_MODE = import.meta.env.VITE_API_MODE === 'mock';
const IS_DEV = import.meta.env.DEV;

let isInitialized = false;
/** True only between startAnalytics() and stopAnalytics(). Every capture checks it. */
let active = false;

function onWindowError(event: ErrorEvent): void {
  captureException(event.error || new Error(event.message), {
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
    unhandled: true,
  });
}

function onUnhandledRejection(event: PromiseRejectionEvent): void {
  const reason = event.reason;
  const err = reason instanceof Error ? reason : new Error(String(reason));
  captureException(err, { unhandledRejection: true });
}

/**
 * Begin collecting. Call only when consent.ts analyticsAllowed() is true.
 * Safe to call repeatedly.
 */
export function startAnalytics(): void {
  if (typeof window === 'undefined' || active) return;
  active = true;
  window.addEventListener('error', onWindowError);
  window.addEventListener('unhandledrejection', onUnhandledRejection);

  if (!POSTHOG_KEY) {
    if (IS_DEV) console.info('[PostHog] VITE_POSTHOG_KEY not set. Events are logged to the console.');
    return;
  }
  if (!isInitialized) {
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      ui_host: 'https://us.posthog.com',
      // localStorage, never a cookie: nothing is sent with requests to us.
      persistence: 'localStorage',
      // While opted out, keep nothing in storage.
      opt_out_persistence_by_default: true,
      autocapture: true,
      mask_all_text: true,
      mask_all_element_attributes: true,
      // Pageviews are sent by PostHogProvider on route changes, sanitised.
      capture_pageview: false,
      capture_pageleave: true,
      rageclick: true,
      capture_performance: false,
      disable_session_recording: true,
      disable_surveys: true,
      before_send: (event) => {
        if (!event) return event;
        event.properties = sanitizeProperties(event.properties ?? {}) as typeof event.properties;
        if (event.$set) event.$set = sanitizeProperties(event.$set);
        if (event.$set_once) event.$set_once = sanitizeProperties(event.$set_once);
        return event;
      },
    });
    posthog.register({
      app_name: 'sv-cloud-web',
      app_version: '0.1.0',
      api_mode: IS_MOCK_MODE ? 'mock' : 'real',
    });
    isInitialized = true;
  }
  // A No given earlier leaves PostHog's own opt-out flag in storage; this
  // Allow replaces it.
  if (posthog.has_opted_out_capturing()) posthog.opt_in_capturing();
}

/** Stop collecting and forget this browser's analytics identity. */
export function stopAnalytics(): void {
  if (typeof window === 'undefined' || !active) return;
  active = false;
  window.removeEventListener('error', onWindowError);
  window.removeEventListener('unhandledrejection', onUnhandledRejection);
  if (POSTHOG_KEY && isInitialized) {
    // reset() BEFORE opt_out: reset clears the stored opt-out too, and
    // PostHog's own autocapture and pageleave would then keep sending.
    posthog.reset();
    posthog.opt_out_capturing();
  }
  clearAnalyticsStorage();
}

/**
 * Remove PostHog's stored identity from this browser. Called on stop, and on
 * every load where the answer is not Allow, so an ID left from an earlier
 * Allow does not outlive a No.
 */
export function clearAnalyticsStorage(): void {
  try {
    for (const key of Object.keys(localStorage)) {
      if (key.startsWith('ph_') && !key.includes('opt_in_out')) localStorage.removeItem(key);
    }
  } catch {
    // Storage unavailable: nothing was stored either.
  }
}

/**
 * Identify a signed-in account. The id and settings only: no email, name or
 * GitHub handle ever reaches PostHog.
 */
export function identifyUser(user: UserTraits): void {
  if (!active) return;
  const traits = {
    plan: user.plan,
    detail_level: user.detailLevel,
    created_at: user.createdAt,
    is_admin: user.isAdmin,
    theme_preference: user.theme,
    direction_preference: user.direction,
    lang: user.lang,
    api_mode: user.apiMode,
  };
  if (POSTHOG_KEY) {
    posthog.identify(user.id, traits);
  } else if (IS_DEV) {
    console.log('[PostHog Identify]', user.id, traits);
  }
}

/**
 * Reset PostHog user identity on logout.
 */
export function resetUser(): void {
  if (POSTHOG_KEY && isInitialized) {
    posthog.reset();
  } else if (IS_DEV) {
    console.log('[PostHog Reset]');
  }
}

/**
 * Associate events with a Project Group (multi-tenancy analytics).
 */
export function groupProject(slug: string, traits?: Partial<ProjectGroupTraits>): void {
  if (!slug || !active) return;
  if (POSTHOG_KEY) {
    posthog.group('project', slug, {
      framework: traits?.framework,
      status: traits?.status,
      region: traits?.region,
      tier: traits?.tier,
      created_at: traits?.createdAt,
      web_address: traits?.webAddress,
    });
  } else if (IS_DEV) {
    console.log('[PostHog Group Project]', slug, traits);
  }
}

/**
 * Associate events with an Account Group.
 */
export function groupAccount(accountId: string, traits?: Record<string, unknown>): void {
  if (!accountId || !active) return;
  if (POSTHOG_KEY) {
    posthog.group('account', accountId, traits);
  } else if (IS_DEV) {
    console.log('[PostHog Group Account]', accountId, traits);
  }
}

/**
 * Capture a custom typed event.
 */
export function captureEvent(eventName: string, properties?: Record<string, unknown>): void {
  if (!active) return;
  if (POSTHOG_KEY) {
    posthog.capture(eventName, properties);
  } else if (IS_DEV) {
    console.log(`[PostHog Event: ${eventName}]`, properties);
  }
}

/**
 * Capture an unhandled exception or caught component crash.
 */
export function captureException(error: Error, extraProps?: Record<string, unknown>): void {
  if (!active) return;
  if (POSTHOG_KEY) {
    posthog.captureException(error, extraProps);
  } else if (IS_DEV) {
    console.error('[PostHog Exception]', error, extraProps);
  }
}

/**
 * Capture an HTTP or API request failure.
 */
export function captureApiError(payload: ApiErrorPayload): void {
  captureEvent('api_request_failed', payload as unknown as Record<string, unknown>);
}

/**
 * Capture a dead click (frustration signal when user clicks non-interactive/disabled element).
 */
export function captureDeadClick(payload: DeadClickPayload): void {
  captureEvent('dead_click', payload as unknown as Record<string, unknown>);
}

/**
 * Capture user stuck / inactivity on a critical flow.
 */
export function captureInactivityStuck(payload: InactivityStuckPayload): void {
  captureEvent('user_inactivity_stuck', payload as unknown as Record<string, unknown>);
}

/**
 * Capture form abandonment when a user edits fields and backs out/dismisses without submitting.
 */
export function captureFormAbandoned(payload: FormAbandonedPayload): void {
  captureEvent('$form_abandoned', payload as unknown as Record<string, unknown>);
  captureEvent('form_abandoned', payload as unknown as Record<string, unknown>);
}

/**
 * Capture form submission.
 */
export function captureFormSubmitted(payload: FormSubmittedPayload): void {
  captureEvent('form_submitted', payload as unknown as Record<string, unknown>);
}

export { posthog };
